import { AutocompleteWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import { useEffect, useMemo, useRef } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { PROVIDERS_SUBMIT_LIST_QUERY } from '../../graphql/accommodationProviderSubmit'
import { getTranslate } from '../../localization'

export type Option = { value: string; label: string }

type Props = {
  accommodation: Option | null
  setAccommodation: (option: Option | null) => void
  provider: Option | null
  setProvider: (option: Option | null) => void
  channel: Option | null
  setChannel: (option: Option | null) => void
  /**
   * Channels come from the diagnostic response itself rather than a separate
   * query, so the picker offers exactly the channels the comparison covered.
   * A channel list from elsewhere could offer one the report has no rows for,
   * which reads as "no data" when it is really "wrong filter".
   */
  channelOptions: Option[]
}

type AccommodationsResponse = {
  accommodations: {
    basicDetails: {
      name: string
      propertyCode: string
      displayPropertyCode: string
    }
  }[]
}

type ProvidersResponse = {
  providersSubmitList: {
    providerCode: string
    displayProviderCode: string
    providerName: string
    providerConnections: {
      status: string
    } | null
  }[]
}

/**
 * AutocompleteWithLabel's props are typed `AutocompleteProps<Option, true, ...>`
 * — the `true` is `multiple` — so its value is declared as an array even though
 * the component works as a single-select and every other screen uses it that
 * way (see MonitoringUpdateFilters). The types are wrong upstream, not the
 * usage.
 *
 * Rather than typing this screen's state as `any` the way the siblings do, the
 * cast is confined to these two props so the state, the options and the
 * handlers stay properly typed.
 */
const single = (value: Option | null) => value as never
const onSingleChange = (set: (option: Option | null) => void) =>
  ((_: unknown, v: Option | null) => set(v)) as never

export default function SelectionFilters({
  accommodation,
  setAccommodation,
  provider,
  setProvider,
  channel,
  setChannel,
  channelOptions
}: Props) {
  const { data: accData, loading: accLoading } =
    useQuery<AccommodationsResponse>(ACCOMMODATIONS_QUERY)

  // Providers are per accommodation, so the query is skipped until one is
  // chosen. Asking for "every provider" would offer codes that do not apply to
  // the selected property, which is the mistake the operator is most likely to
  // make unaided.
  const { data: providerData, loading: providerLoading } =
    useQuery<ProvidersResponse>(PROVIDERS_SUBMIT_LIST_QUERY, {
      variables: { propertyCode: accommodation?.value },
      skip: accommodation == null
    })

  const accommodationOptions: Option[] = useMemo(
    () =>
      (accData?.accommodations ?? [])
        .filter((a) => a.basicDetails?.propertyCode)
        .map((a) => ({
          value: a.basicDetails.propertyCode,
          // The displayed code is what the operator sees in every other screen
          // and in the support ticket, so it belongs in the label.
          label: `${a.basicDetails.name} (${
            a.basicDetails.displayPropertyCode || a.basicDetails.propertyCode
          })`
        })),
    [accData]
  )

  const providerOptions: Option[] = useMemo(
    () =>
      (providerData?.providersSubmitList ?? []).map((p) => ({
        value: p.providerCode,
        label: `${p.providerName} (${p.displayProviderCode || p.providerCode})${
          p.providerConnections?.status === 'Active'
            ? ''
            : ` — ${getTranslate('inactive')}`
        }`
      })),
    [providerData]
  )

  // URL restoration starts with code-only placeholder options. Replace those
  // with the canonical labels once each dependent option query resolves.
  useEffect(() => {
    const restored = accommodationOptions.find(
      (option) => option.value === accommodation?.value
    )
    if (restored && restored.label !== accommodation?.label) {
      setAccommodation(restored)
    }
  }, [accommodation, accommodationOptions, setAccommodation])

  useEffect(() => {
    const restored = providerOptions.find(
      (option) => option.value === provider?.value
    )
    if (restored && restored.label !== provider?.label) {
      setProvider(restored)
    }
  }, [provider, providerOptions, setProvider])

  useEffect(() => {
    const restored = channelOptions.find(
      (option) => option.value === channel?.value
    )
    if (restored && restored.label !== channel?.label) {
      setChannel(restored)
    }
  }, [channel, channelOptions, setChannel])

  // Changing the accommodation invalidates both downstream selections. Leaving
  // a stale provider code would run the diagnostic against a property that has
  // no such provider and report an empty window, which looks like a data fault.
  const previousAccommodation = useRef(accommodation?.value)
  useEffect(() => {
    if (
      previousAccommodation.current != null &&
      previousAccommodation.current !== accommodation?.value
    ) {
      setProvider(null)
      setChannel(null)
    }
    previousAccommodation.current = accommodation?.value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accommodation?.value])

  // A single provider is the common case. Selecting it removes a step that has
  // exactly one correct answer.
  useEffect(() => {
    if (provider == null && providerOptions.length === 1) {
      setProvider(providerOptions[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerOptions])

  return (
    <>
      <AutocompleteWithLabel
        label={getTranslate('Accommodation')}
        inputSize="small"
        placeholder={getTranslate('Select an accommodation')}
        loading={accLoading}
        value={single(accommodation)}
        onChange={onSingleChange(setAccommodation)}
        options={accommodationOptions}
        getOptionLabel={(option: Option) => option.label || ''}
        getOptionSelected={(option: Option, value: Option) =>
          option.value === value.value
        }
      />

      <AutocompleteWithLabel
        label={getTranslate('Provider')}
        inputSize="small"
        placeholder={
          accommodation == null
            ? getTranslate('Select an accommodation first')
            : getTranslate('Select a provider')
        }
        disabled={accommodation == null}
        loading={providerLoading}
        value={single(provider)}
        onChange={onSingleChange(setProvider)}
        options={providerOptions}
        getOptionLabel={(option: Option) => option.label || ''}
        getOptionSelected={(option: Option, value: Option) =>
          option.value === value.value
        }
      />

      <AutocompleteWithLabel
        label={getTranslate('Channel to fetch (optional)')}
        inputSize="small"
        placeholder={
          channelOptions.length === 0
            ? getTranslate('Run the diagnostic to list channels')
            : getTranslate('All channels')
        }
        disabled={channelOptions.length === 0}
        value={single(channel)}
        onChange={onSingleChange(setChannel)}
        options={channelOptions}
        getOptionLabel={(option: Option) => option.label || ''}
        getOptionSelected={(option: Option, value: Option) =>
          option.value === value.value
        }
      />
    </>
  )
}
